import { Icon, IconButton, Text } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import { CountryLiability } from "./CountryLiability"
import { useState } from "react"
import { CountryNoteGroup } from "@oysterhr/schema"
import CountryNotes from "./CountryNotes"
import classNames from "classnames"
import { CountryLiabilityTier, useKnowledgeBaseUrl } from "../../utils"

export type CountryInformationProps = {
  countryCode?: string
  countryName?: string
  countryNotes?: CountryNoteGroup[] | null
  countryLiabilityLink?: string
  countryLiabilityTier?: CountryLiabilityTier
}

export function CountryInformation({
  countryCode,
  countryName,
  countryNotes,
  countryLiabilityTier,
  countryLiabilityLink,
}: CountryInformationProps) {
  const { t } = useTranslation("CostCalculator")
  const { data } = useKnowledgeBaseUrl(countryCode as string, {})
  const [showNotes, setShowNotes] = useState<boolean>(true)

  const toggleNotes = () => {
    setShowNotes(prevState => !prevState)
  }

  const showLiability = countryLiabilityTier && countryLiabilityTier !== "UNKNOWN"

  return (
    <div data-testid="countrInformation" className={classNames("w-full px-1 text-center")}>
      <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0">
        <div className="relative flex flex-row justify-center">
          <Text className="self-center text-left">
            {t("countryInfoLabel", { country: countryName })}
          </Text>
          <IconButton
            variation="borderless"
            aria-label="Toggle notes"
            iconName="ChevronDown"
            style={{
              transform: showNotes ? "rotate(-180deg)" : "rotate(0deg)",
            }}
            className="absolute right-2 top-2 text-gray-400 transition-transform lg:hidden"
            onClick={toggleNotes}
            size="md"
          />
        </div>
        <div className=" flex flex-col items-center space-y-3 lg:ml-auto lg:flex-row lg:space-y-0">
          {data?.knowledgeBaseUrl && (
            <a
              href={data.knowledgeBaseUrl}
              target="_blank"
              className="flex flex-row items-center space-x-1 text-primary-base lg:ml-auto"
            >
              <Text size="small" className="underline">
                {t("viewCountryProfile")}
              </Text>
              <Icon size="sm" name="ArrowSquareOut" />
            </a>
          )}
          {showLiability && data?.knowledgeBaseUrl && (
            <div className="mx-4 hidden lg:block">
              <Text size="small" className="text-xs text-gray-400 md:text-sm">
                /
              </Text>
            </div>
          )}
          {showLiability && (
            <CountryLiability link={countryLiabilityLink} tier={countryLiabilityTier} />
          )}
        </div>
        <div className="flex items-center">
          {/* Show this icon only in desktop, in mobile we show in a different place */}
          <IconButton
            variation="borderless"
            aria-label="Toggle notes"
            iconName="ChevronDown"
            style={{
              transform: showNotes ? "rotate(-180deg)" : "rotate(0deg)",
            }}
            className="ml-4 hidden text-gray-400 transition-transform lg:block"
            onClick={toggleNotes}
            size="md"
          />
        </div>
      </div>
      {showNotes && (
        <div className="mt-4 flex flex-col items-start">
          {countryNotes?.map(noteGroup => (
            <CountryNotes noteGroup={noteGroup} key={noteGroup.name} />
          ))}
        </div>
      )}
    </div>
  )
}
